import { PreviewContainer } from 'modules/templateEditor/common';
import { CSSProperties } from 'react';
import styled from 'styled-components';
import {
  Body2,
  body2,
  caption,
  colors,
  fontWeights,
  letterSpacing,
  shadows,
  spacing,
  subtitle1,
  subtleShadow,
} from 'ui/theme';
import { pxToRem } from 'utils/convert';

export const containerStyles: CSSProperties = {
  display: `flex`,
  height: `100%`,
  justifyContent: `center`,
};

export const deviceStylesCommon: CSSProperties = {
  alignItems: `center`,
  borderRadius: pxToRem(20),
  display: `flex`,
  flexDirection: `column`,
  height: pxToRem(650),
  letterSpacing: letterSpacing.pos10,
  overflow: `hidden`,
  width: pxToRem(360),
};

export const deviceStylesUnset: CSSProperties = {
  backgroundColor: colors.white,
  // note: outline is being applied here as box-shadow because Safari doesn't apply border-radius to outline
  boxShadow: [...shadows.subtle, `0 0 0 ${spacing.eighth} ${colors.grey25}`].join(', '),
  justifyContent: `center`,
  textAlign: `center`,
};

/**
 * This needs to be wrapped in styled because it's
 * used to apply additional styles to PreviewContainer
 * in other modules.
 */
export const WhatsAppPreviewContainer = styled(PreviewContainer)`
  min-height: ${pxToRem(780)};
  @media (min-width: 1350px) {
    min-width: ${pxToRem(760)};
  }
`;

export const PreviewContent = styled.div`
  flex: 1 1 auto;
  padding-top: ${spacing.plusHalf};
`;

export const PreviewFooter = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: ${spacing.base} 0;
  padding-bottom: ${spacing.half};
`;

export const PreviewCharacterCount = styled(Body2)`
  color: ${colors.textSecondary};
  margin: 0;
  display: flex;
  align-items: center;
  &:not(:first-child):before {
    margin: 0 ${spacing.half};
    content: '•';
  }
  &.isWarning {
    & > :last-child {
      margin-right: ${spacing.half};
    }
    color: ${colors.textWarning};
  }
`;

export const messageUnset: CSSProperties = {
  ...body2,
  color: colors.textSecondary,
  margin: `0 ${spacing.plusHalf}`,
};

export const rotate180: CSSProperties = {
  transform: `rotate(180deg)`,
};

export const chatTick: CSSProperties = {
  width: `0px`,
  height: `0px`,
  borderBottom: `10px solid transparent`,
  borderRight: `10px solid ${colors.white}`,
  float: `left`,
};

export const senderChatTick: CSSProperties = {
  width: `0px`,
  height: `0px`,
  borderBottom: `10px solid transparent`,
  borderLeft: `10px solid #DCD4CB`,
  float: `right`,
};

export const whatsAppButton: CSSProperties = {
  display: `flex`,
  backgroundColor: colors.white,
  borderRadius: pxToRem(4),
  width: '80%',
  alignItems: `center`,
  justifyContent: `center`,
  padding: pxToRem(8),
  marginLeft: pxToRem(10),
  marginTop: pxToRem(4),
  gap: pxToRem(4),
  ...body2,
  color: `#0096DE`,
  minHeight: pxToRem(36),
  flexShrink: 0,
};

// Message Specific

export const messageBody: CSSProperties = {
  alignItems: `flex-start`,
  boxSizing: `border-box`,
  display: `flex`,
  flex: `1 1 auto`,
  flexDirection: `column`,
  fontWeight: fontWeights.regular,
  overflowX: `hidden`,
  overflowY: `auto`,
  padding: ` ${pxToRem(12)} ${pxToRem(4)}`,
  width: `100%`,
};

export const messageHeaderWhatsApp: CSSProperties = {
  alignItems: `center`,
  backgroundColor: `#085B53`,
  boxSizing: `border-box`,
  display: `flex`,
  flex: `0 0 auto`,
  height: pxToRem(60),
  paddingTop: pxToRem(8),
  width: `100%`,
  ...subtleShadow,
};

export const messageContentHeader: CSSProperties = {
  ...body2,
  fontWeight: fontWeights.semiBold,
  marginBottom: pxToRem(8),
};

export const messageHeaderWhatsAppSide: CSSProperties = {
  display: `flex`,
  flex: `0 0 ${pxToRem(48)}`,
  justifyContent: `center`,
};

export const messageHeaderWhatsAppCenter: CSSProperties = {
  display: `flex`,
  flex: `1 1 auto`,
  justifyContent: `start`,
  ...subtitle1,
  color: colors.white,
};

export const previewInfoMessage: CSSProperties = {
  display: `flex`,
  gap: spacing.half,
  alignItems: `center`,
  justifyContent: `center`,
  margin: `${spacing.plusHalf}`,

  ...caption,
  color: colors.textSecondary,
};

export const messageBodyMedia: CSSProperties = {
  borderRadius: pxToRem(7),
  flex: `0 0 auto`,
  marginBottom: pxToRem(8),
  width: '100%',
};

export const messageBodyWhatsAppMessageContainer: CSSProperties = {
  display: `flex`,
  flex: `0 0 auto`,
  lineHeight: 1.4,
  width: '100%',
};

export const messageBodyWhatsAppMessageSenderContainer: CSSProperties = {
  ...messageBodyWhatsAppMessageContainer,
  alignSelf: `flex-end`,
  justifyContent: 'flex-end',
  marginTop: pxToRem(7),
  marginBottom: pxToRem(8),
};

export const messageBodyWhatsAppMessage: CSSProperties = {
  backgroundColor: colors.white,
  borderRadius: pxToRem(4),
  borderTopLeftRadius: 0,
  color: colors.black,
  flex: `0 0 auto`,
  fontSize: pxToRem(12),
  width: '80%',
  overflowWrap: `break-word`,
  padding: pxToRem(8),
  whiteSpace: `pre-wrap`,
};

export const messageBodyWhatsAppSenderMessage: CSSProperties = {
  ...messageBodyWhatsAppMessage,
  backgroundColor: `#DCD4CB`,
  borderTopLeftRadius: pxToRem(4),
  borderTopRightRadius: 0,
};

export const whatsAppSkeletonBarSenderContent: CSSProperties = {
  height: pxToRem(6),
  backgroundColor: `rgba(0, 0, 0, 0.07)`,
  borderRadius: pxToRem(8),
  marginBottom: pxToRem(6),
};

export const whatsAppSkeletonBarHalfSenderContent: CSSProperties = {
  ...whatsAppSkeletonBarSenderContent,
  width: `50%`,
  marginBottom: `0`,
};

export const messageBodyWhatsAppMessageTime: CSSProperties = {
  color: colors.grey60,
  flex: `0 0 auto`,
  fontSize: pxToRem(10),
  textAlign: `right`,
};

export const messageContent = {
  backgroundColor: `#ECE5DD`,
  borderColor: `transparent`,
  // note: outline is being applied here as box-shadow because Safari doesn't apply border-radius to outline
  boxShadow: [...shadows.subtle, `0 0 0 ${spacing.eighth} ${colors.grey25}`].join(', '),
  ...body2,
};

export const previewFooter: CSSProperties = {
  ...caption,
  color: `${colors.textSecondary}`,
  marginTop: pxToRem(4),
};

export const flexDirectionColumn: CSSProperties = {
  flexDirection: `column`,
};

export const linkText: CSSProperties = {
  color: `${colors.link}`,
  textDecoration: `none`,
  textOverflow: `ellipsis`,
  overflow: `hidden`,
  alignSelf: `stretch`,
  whiteSpace: `pre`,
};
